import React, { useState, useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { Shield } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

// Lazy load PayPal components
const PayPalScriptProvider = lazy(() => import("@paypal/react-paypal-js").then(m => ({ default: m.PayPalScriptProvider })));
const PayPalButtons = lazy(() => import("@paypal/react-paypal-js").then(m => ({ default: m.PayPalButtons })));

// Lazy load confetti only when needed
let confetti = null;
const loadConfetti = async () => {
  if (!confetti) {
    const module = await import("canvas-confetti");
    confetti = module.default;
  }
  return confetti;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayPalCheckoutModal = ({ isOpen, onClose, onSuccess }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1 = shipping info, 2 = payment
  const [paypalConfig, setPaypalConfig] = useState({ client_id: "sb", mode: "sandbox" });
  const [shipping, setShipping] = useState({
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    country: "France"
  });
  const [errors, setErrors] = useState({});

  // Fetch PayPal config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/paypal/config`);
        setPaypalConfig(response.data);
      } catch (err) {
        console.error("Failed to fetch PayPal config:", err);
      }
    };
    fetchConfig();
  }, []);

  const validateShipping = () => {
    const newErrors = {};
    if (!shipping.phone || shipping.phone.length < 10) {
      newErrors.phone = language === 'fr' ? 'Numéro de téléphone requis' : 'Phone number required';
    }
    if (!shipping.address) {
      newErrors.address = language === 'fr' ? 'Adresse requise' : 'Address required';
    }
    if (!shipping.city) {
      newErrors.city = language === 'fr' ? 'Ville requise' : 'City required';
    }
    if (!shipping.postal_code || shipping.postal_code.length < 4) {
      newErrors.postal_code = language === 'fr' ? 'Code postal requis' : 'Postal code required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateShipping()) {
      setStep(2);
    }
  };

  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [{
        amount: {
          value: "10.00",
          currency_code: "EUR"
        },
        description: "Calli - Preorder Deposit"
      }]
    });
  };

  const onApprove = async (data, actions) => {
    try {
      const details = await actions.order.capture();
      
      // Create preorder in our backend with shipping info
      await axios.post(`${API}/preorders`, {
        paypal_order_id: details.id,
        amount: 15.0,
        shipping: shipping
      }, { withCredentials: true });

      // Fire confetti! (lazy loaded)
      const confettiModule = await loadConfetti();
      confettiModule({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Payment error:", error);
      alert(language === 'fr' ? "Erreur de paiement. Veuillez réessayer." : "Payment failed. Please try again.");
    }
  };

  // Reset step when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setErrors({});
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="paypal-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-gray-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {step === 1 
              ? (language === 'fr' ? '📦 Adresse de livraison' : '📦 Shipping Address')
              : t("preorder_cta")
            }
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-500 text-center">
              {language === 'fr' 
                ? 'Pour recevoir votre Calli, renseignez vos coordonnées'
                : 'To receive your Calli, enter your details'}
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'fr' ? 'Téléphone' : 'Phone'} *
              </label>
              <input
                type="tel"
                value={shipping.phone}
                onChange={(e) => setShipping({...shipping, phone: e.target.value})}
                placeholder="+33 6 12 34 56 78"
                className={`w-full px-4 py-3 rounded-xl border ${errors.phone ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-orange-500`}
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'fr' ? 'Adresse' : 'Address'} *
              </label>
              <input
                type="text"
                value={shipping.address}
                onChange={(e) => setShipping({...shipping, address: e.target.value})}
                placeholder={language === 'fr' ? '123 Rue de la Paix' : '123 Main Street'}
                className={`w-full px-4 py-3 rounded-xl border ${errors.address ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-orange-500`}
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Code postal' : 'Postal Code'} *
                </label>
                <input
                  type="text"
                  value={shipping.postal_code}
                  onChange={(e) => setShipping({...shipping, postal_code: e.target.value})}
                  placeholder="75001"
                  className={`w-full px-4 py-3 rounded-xl border ${errors.postal_code ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-orange-500`}
                />
                {errors.postal_code && <p className="text-red-500 text-xs mt-1">{errors.postal_code}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Ville' : 'City'} *
                </label>
                <input
                  type="text"
                  value={shipping.city}
                  onChange={(e) => setShipping({...shipping, city: e.target.value})}
                  placeholder="Paris"
                  className={`w-full px-4 py-3 rounded-xl border ${errors.city ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-orange-500`}
                />
                {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'fr' ? 'Pays' : 'Country'}
              </label>
              <select
                value={shipping.country}
                onChange={(e) => setShipping({...shipping, country: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 bg-white"
              >
                <option value="France">France</option>
                <option value="Belgique">Belgique</option>
                <option value="Suisse">Suisse</option>
                <option value="Luxembourg">Luxembourg</option>
                <option value="Canada">Canada</option>
              </select>
            </div>

            <button
              onClick={handleContinue}
              className="w-full calli-btn-primary mt-4"
            >
              {language === 'fr' ? 'Continuer vers le paiement' : 'Continue to payment'} →
            </button>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div className="text-center space-y-2">
              <span className="text-4xl font-bold text-orange-500">15€</span>
              <p className="text-gray-500">{t("preorder_price_info")}</p>
            </div>

            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-sm text-center text-gray-600">
                <Shield className="w-4 h-4 inline mr-1 text-green-600" />
                {t("preorder_guarantee")}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 text-sm">
              <p className="font-medium text-gray-700">{language === 'fr' ? 'Livraison à :' : 'Shipping to:'}</p>
              <p className="text-gray-600">{shipping.address}</p>
              <p className="text-gray-600">{shipping.postal_code} {shipping.city}, {shipping.country}</p>
              <p className="text-gray-600">{shipping.phone}</p>
              <button 
                onClick={() => setStep(1)}
                className="text-orange-500 text-xs mt-2 hover:underline"
              >
                {language === 'fr' ? 'Modifier' : 'Edit'}
              </button>
            </div>

            <Suspense fallback={<div className="text-center py-4">Chargement...</div>}>
              <PayPalScriptProvider options={{ 
                clientId: paypalConfig.client_id || "sb",
                currency: "EUR"
              }}>
                <PayPalButtons
                  createOrder={createOrder}
                  onApprove={onApprove}
                  style={{ layout: "vertical", shape: "pill" }}
                  data-testid="paypal-buttons"
                />
              </PayPalScriptProvider>
            </Suspense>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PayPalCheckoutModal;
